/**
 * Composant de drag & drop pour l'upload de fichiers Excel.
 * Ce fichier est NOUVEAU et n'affecte pas les composants existants.
 */
import React, { useRef, useState } from "react";
import "./ExcelDropzone.css";

const ExcelDropzone = ({ onFileSelect, acceptedFormats = ".xlsx,.xls" }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = (file) => {
    setError(null);

    // Valider le format
    const validExtensions = acceptedFormats.split(",").map((ext) => ext.trim());
    const fileName = file.name.toLowerCase();
    const isValidFormat = validExtensions.some((ext) => fileName.endsWith(ext));

    if (!isValidFormat) {
      setError(`Format invalide. Formats acceptés : ${acceptedFormats}`);
      return;
    }

    // Valider la taille (max 10 MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError("Fichier trop volumineux. Taille maximale : 10 MB");
      return;
    }

    setSelectedFile(file);
    onFileSelect(file);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setSelectedFile(null);
    setError(null);
    onFileSelect(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="excel-dropzone-container">
      <div
        className={`excel-dropzone ${isDragging ? "dragging" : ""} ${
          selectedFile ? "has-file" : ""
        } ${error ? "has-error" : ""}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFormats}
          onChange={handleFileInput}
          style={{ display: "none" }}
        />

        {!selectedFile && !error && (
          <div className="dropzone-content">
            <div className="dropzone-icon">📁</div>
            <div className="dropzone-text">
              <p className="dropzone-title">
                Glissez-déposez votre fichier Excel ici
              </p>
              <p className="dropzone-subtitle">
                ou cliquez pour sélectionner un fichier
              </p>
              <p className="dropzone-formats">
                Formats acceptés : {acceptedFormats}
              </p>
            </div>
          </div>
        )}

        {selectedFile && !error && (
          <div className="dropzone-file">
            <div className="file-icon">📄</div>
            <div className="file-info">
              <p className="file-name">{selectedFile.name}</p>
              <p className="file-size">
                {(selectedFile.size / 1024).toFixed(2)} KB
              </p>
            </div>
            <button
              className="file-remove"
              onClick={handleRemove}
              type="button"
            >
              ✕
            </button>
          </div>
        )}

        {error && (
          <div className="dropzone-error">
            <div className="error-icon">⚠️</div>
            <p className="error-message">{error}</p>
            <button
              className="error-retry"
              onClick={(e) => {
                e.stopPropagation();
                setError(null);
                setSelectedFile(null);
              }}
              type="button"
            >
              Réessayer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExcelDropzone;
